/* eslint-disable */
import { Regions, isSameRegion } from '@smh/projects/regions';
import { State, Mutation, Action, Getter } from 'vuex-simple';

import { RootModule } from '@jtnews/core';
import {
  PASSWORD_ALLOW_SYMBOLS_REG_EX,
  SYMBOLS_REG_EX,
  LETTERS_REG_EX,
  NUMBERS_REG_EX,
  EMAIL_REGEX,
  EMAIL_SGP_DOMAIN_REGEX,
  HTML_TAGS_REGEX
} from '@jtnews/shared/data';
import { UserInfo } from '@jtnews/shared/news';

import { SocialsData } from '../../domain';
import {
  UserDataMapperService,
  userDataMapperService,
  ApiDataErrorDTO
} from '../../infrastructure';

const INITIAL_SOCIAL_LIST = [
  {
    name: 'google',
    id: 'google',
    link: '',
    type: 'google-colored',
    title: 'Google',
    fill: 'none',
    reserveFill: 'none',
    width: 24,
    height: 24
  },
  {
    name: 'vk',
    id: 'vk',
    link: '',
    type: 'vk',
    title: 'ВКонтакте',
    fill: '#4A76A8',
    reserveFill: '#4A76A8',
    width: 24,
    height: 15
  },
  {
    name: 'fb',
    id: 'fb',
    link: '',
    type: 'fb',
    title: 'fb',
    fill: '#FFFFFF',
    reserveFill: '#4267B2',
    width: 15,
    height: 24
  },
  {
    name: 'yandex',
    id: 'ya',
    link: '',
    type: 'yandex',
    title: 'Яндекс',
    fill: '#FFFFFF',
    reserveFill: '#FF0000',
    width: 12,
    height: 24
  },
  {
    name: 'ok',
    id: 'ok',
    link: '',
    type: 'ok',
    title: 'Одноклассники',
    fill: '#FFFFFF',
    reserveFill: '#EE8208',
    width: 14,
    height: 24
  }
];
const RESTRICTED_SOCIALS = ['fb'];

const CAPTCHA_AUTH_ACTION = 'user_auth';
const CAPTCHA_REGISTER_ACTION = 'user_register';
const CAPTCHA_CONFIRM_REGISTER_ACTION = 'confirm_user_register';

const AUTH_MIN_LENGTH = 2;
const EMAIL_MIN_LENGTH_ERROR = 0;
const REGISTER_PASSWORD_MIN_LENGTH = 6;
const REGISTER_PASSWORD_MAX_LENGTH = 20;

const DEFAULT_API_ERROR_TEXT = 'Произошла ошибка. Попробуйте авторизоваться позже.';
const EMPTY_FIELD_ERROR = 'Необходимо заполнить поле';
const FIELD_XSS_ERROR = 'содержит недопустимый набор символов';
const EMAIL_EMPTY_ERROR = 'Введите E-mail';
const EMAIL_REGEX_ERROR = 'Введите верный E-mail';
const EMAIL_SGP_DOMAIN_ERROR =
  'Если Вы хотите зарегистрировать e-mail, то перейдите на <a href="%URL%">форму регистрации</a> e-mail';
const MIN_LENGTH_REGISTER_PASSWORD_ERROR = `Пароль должен быть не менее ${REGISTER_PASSWORD_MIN_LENGTH} символов`;
const MAX_LENGTH_REGISTER_PASSWORD_ERROR = `Пароль должен быть не больше ${REGISTER_PASSWORD_MAX_LENGTH} символов`;
const REGISTER_PASSWORD_SYMBOLS_ERROR =
  'Пароль не должен совпадать с логином и должен содержать от 6 до 20 символов — латинские буквы, цифры и спецсимволы (допускаются знаки ` ! @ # $ % ^ & * ( ) - _ = + [ ] { } ; : " \\ | , . < > / ?, не допускаются ~ и \').';
const REGISTER_PASSWORD_LIKE_LOGIN_ERROR = 'Пароль не должен совпадать с логином';

export class UsersStore {
  private _userDataMapperService: UserDataMapperService = userDataMapperService;

  constructor(private _root: RootModule) {}

  @State()
  private _authLogin = '';
  @State()
  private _authPassword = '';
  @State()
  private _registerEmail = '';
  @State()
  private _registerPassword = '';
  @State()
  private _authApiError = {
    isCaptcha: false,
    text: ''
  };
  @State()
  private _registerApiError = '';
  @State()
  private _confirmRegisterApiError = '';
  @State()
  private _isRegisterThroughSocials = false;

  @Getter()
  public get authLoginError(): string {
    const login = this._authLogin;

    if (login.trim().length <= AUTH_MIN_LENGTH) {
      return EMPTY_FIELD_ERROR;
    }

    if (HTML_TAGS_REGEX.exec(login) !== null) {
      return `Логин ${FIELD_XSS_ERROR}`;
    }

    return '';
  }
  @Getter()
  public get authPasswordError(): string {
    const password = this._authPassword;

    if (password.trim().length <= AUTH_MIN_LENGTH) {
      return EMPTY_FIELD_ERROR;
    }

    if (HTML_TAGS_REGEX.exec(password) !== null) {
      return `Пароль ${FIELD_XSS_ERROR}`;
    }

    return '';
  }
  @Getter()
  public get authApiErrorText(): string {
    return this._authApiError.text;
  }
  @Getter()
  public get isCaptchaApiError(): boolean {
    return this._authApiError.isCaptcha;
  }
  @Getter()
  public get registerEmail(): string {
    return this._registerEmail;
  }
  @Getter()
  public get registerPassword(): string {
    return this._registerPassword;
  }
  @Getter()
  public get registerEmailError(): string {
    const email = this._registerEmail;

    if (email.trim().length === EMAIL_MIN_LENGTH_ERROR) {
      return EMAIL_EMPTY_ERROR;
    }

    if (!EMAIL_REGEX.test(email)) {
      return EMAIL_REGEX_ERROR;
    }

    if (EMAIL_SGP_DOMAIN_REGEX.test(email)) {
      const mailRegisterUrl = this._root.commonModule.authUrls?.mailRegister || '';
      return EMAIL_SGP_DOMAIN_ERROR.replace('%URL%', mailRegisterUrl);
    }

    if (HTML_TAGS_REGEX.exec(email) !== null) {
      return `E-mail ${FIELD_XSS_ERROR}`;
    }

    return '';
  }
  @Getter()
  public get registerPasswordError(): string {
    const password = this._registerPassword;

    if (password.length < REGISTER_PASSWORD_MIN_LENGTH) {
      return MIN_LENGTH_REGISTER_PASSWORD_ERROR;
    }

    if (password.length > REGISTER_PASSWORD_MAX_LENGTH) {
      return MAX_LENGTH_REGISTER_PASSWORD_ERROR;
    }

    if (
      password === this._registerEmail ||
      password === this._registerEmail.split('@')[0]
    ) {
      return REGISTER_PASSWORD_LIKE_LOGIN_ERROR;
    }

    if (!this._isPasswordValid(password)) {
      return REGISTER_PASSWORD_SYMBOLS_ERROR;
    }

    if (HTML_TAGS_REGEX.exec(password) !== null) {
      return `E-mail ${FIELD_XSS_ERROR}`;
    }

    return '';
  }
  @Getter()
  public get registerApiError(): string {
    return this._registerApiError;
  }
  @Getter()
  public get confirmRegisterApiError(): string {
    return this._confirmRegisterApiError;
  }
  @Getter()
  public get isRegisterThroughSocials(): boolean {
    return this._isRegisterThroughSocials;
  }
  @Getter()
  public get authLinks(): Record<string, string> {
    return this._root.commonModule.authUrls || {};
  }
  @Getter()
  public get socialsList(): SocialsData[] {
    const socials = INITIAL_SOCIAL_LIST.filter(
      (item: SocialsData) => this.authLinks[item.id]?.length > 0
    ).sort((a, b) => {
      if (RESTRICTED_SOCIALS.includes(a.id)) {
        return 1;
      }
      if (RESTRICTED_SOCIALS.includes(b.id)) {
        return -1;
      }
      return 0;
    });

    return socials.map(item => ({
      ...item,
      link: this.authLinks[item.id]
    }));
  }
  @Getter()
  public get socialsListWithoutRestricted(): SocialsData[] {
    const socials = INITIAL_SOCIAL_LIST.filter(
      (item: SocialsData) =>
        this.authLinks[item.id]?.length > 0 && !RESTRICTED_SOCIALS.includes(item.id)
    );

    return socials.map(item => ({
      ...item,
      link: this.authLinks[item.id]
    }));
  }

  @Action()
  public async checkAuth(norequest = false): Promise<void> {
    if (!norequest) {
      const data = await this._userDataMapperService.checkAuthorization();
      this._root.commonModule.setUserInfo(data as UserInfo | null);
      void this._root.commonModule.fetchNewProfileNotices();

      if (data !== null) {
        this._root.commonModule.runUserStatusSocket(data.userId);
      } else {
        this._root.commonModule.userModerationStatusService.closeWS();
      }
    }

    this._root.commonModule.sendUserAuthMetric();
    this._root.commonModule.setAuthChecked(true);
  }
  @Action()
  public async checkAuthOnForumNN(): Promise<void> {
    if (isSameRegion(this._root.regionId, Regions.NizhnyNovgorod)) {
      const data = await this._userDataMapperService.checkAuthOnForumNN();

      this._root.commonModule.setForumProfile(data);
      this._root.commonModule.setAuthForumChecked(true);
      this._root.commonModule.sendNNForumUserAuthMetric();
      void this._root.commonModule.checkSpWidgetNotify();
    }
  }
  @Action()
  public async authorization(typeProfile: string): Promise<void> {
    this.setAuthApiError({
      isCaptcha: false,
      text: ''
    });

    if (typeProfile !== 'forums') {
      try {
        await this.authorizationForNews();
      } catch (err) {
        throw (err as ApiDataErrorDTO).status;
      }
    }

    if (typeProfile !== 'news') {
      await this.authForForumNN();
    }
  }
  @Action()
  public async authorizationForNews(): Promise<void> {
    const captchaToken = (await this._executeCaptchaToken(CAPTCHA_AUTH_ACTION)) || '';
    const username = this._encodeUnicode(this._authLogin);
    const password = this._encodeUnicode(this._authPassword);

    try {
      const data = await this._userDataMapperService.login(
        { username, password },
        captchaToken
      );

      this._root.commonModule.setUserInfo(data);
      this._root.commonModule.runUserStatusSocket(data?.userId || 0);
      void this._root.commonModule.fetchNewProfileNotices();

      this._setBookmarks();

      if (this._root.pushModule.isPushNotificationsEnabled) {
        this._root.pushModule.saveUserPushNotificationBrowserSettings({
          profileId: data.userId.toString(),
          isAfterAuth: true
        });
      }

      this._root.commonModule.sendUserAuthMetric();

      this._root.commonModule.setUserAuthStateToLocalStorage(true);
    } catch (err) {
      this._root.commonModule.userModerationStatusService.closeWS();

      const error = err as ApiDataErrorDTO;
      this.setAuthApiError({
        isCaptcha: error.status === 403,
        text: error.detail || DEFAULT_API_ERROR_TEXT
      });
      throw error;
    }
  }
  @Action()
  public async authForForumNN(): Promise<void> {
    const username = this._encodeUnicode(this._authLogin);
    const password = this._encodeUnicode(this._authPassword);

    try {
      const data = await this._userDataMapperService.authForForumNN(username, password);

      this._root.commonModule.setForumProfile(data);
      this._root.commonModule.sendNNForumUserAuthMetric();
      void this._root.commonModule.checkSpWidgetNotify();
    } catch (err) {
      const error = err as ApiDataErrorDTO;
      this.setAuthApiError({
        isCaptcha: error.status === 403,
        text: error.detail || DEFAULT_API_ERROR_TEXT
      });
      throw error;
    }
  }
  @Action()
  public authThroughSocialsNN(url: string): void {
    const intervalTimeoutMs = 500;
    const params =
      'height=500,width=500,resizable=yes,scrollbars=yes,menubar=no,status=yes,top=100,left=100';
    const win = window.open(url, 'nn_socials', params);
    win?.focus();

    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    const intervalId = setInterval(async () => {
      if (typeof win === 'undefined' || win?.closed) {
        clearInterval(intervalId);
        await this._userDataMapperService.authThroughSocialsNN();

        window.location.reload();
      }
      // tslint:disable-next-line: no-magic-numbers
    }, intervalTimeoutMs);
  }
  @Action()
  public async register(): Promise<void> {
    this.setRegisterApiError('');

    const captchaToken = (await this._executeCaptchaToken(CAPTCHA_REGISTER_ACTION)) || '';

    try {
      await this._userDataMapperService.register(
        this._registerEmail,
        this._registerPassword,
        captchaToken
      );
    } catch (err) {
      const detail = (err as ApiDataErrorDTO).detail || DEFAULT_API_ERROR_TEXT;
      this.setRegisterApiError(detail);
      throw err;
    }
  }
  @Action()
  public async confirmNotifyEmail(email: string): Promise<void> {
    const captchaToken =
      (await this._executeCaptchaToken(CAPTCHA_CONFIRM_REGISTER_ACTION)) || '';
    this.setConfirmRegisterApiError('');

    try {
      await this._userDataMapperService.sendConfirmRegistration(email, captchaToken);
    } catch (err) {
      const detail = (err as ApiDataErrorDTO).detail || DEFAULT_API_ERROR_TEXT;

      this.setConfirmRegisterApiError(detail);
      throw err;
    }
  }

  @Mutation()
  public setAuthLogin(login: string): void {
    this._authLogin = login;
  }
  @Mutation()
  public setAuthPassword(password: string): void {
    this._authPassword = password;
  }
  @Mutation()
  public setAuthApiError(error: { isCaptcha: boolean; text: string }): void {
    this._authApiError = { ...error };
  }
  @Mutation()
  public resetAuthApiError(): void {
    this._authApiError = { isCaptcha: false, text: '' };
  }
  @Mutation()
  public setRegisterEmail(email: string): void {
    this._registerEmail = email;
  }
  @Mutation()
  public setRegisterPassword(password: string): void {
    this._registerPassword = password;
  }
  @Mutation()
  public setRegisterApiError(error: string): void {
    this._registerApiError = error;
  }
  @Mutation()
  public setConfirmRegisterApiError(error: string): void {
    this._confirmRegisterApiError = error;
  }
  @Mutation()
  public setRegisterThroughSocialsFlag(value: boolean): void {
    this._isRegisterThroughSocials = value;
  }

  private _setBookmarks() {
    const { userInfo } = this._root.commonModule;
    const bookmarks =
      userInfo !== null && userInfo.bookmarks !== null ? userInfo.bookmarks : [];
    this._root.userModule.updateBookmarksState(bookmarks);
  }

  private _isPasswordValid(password: string): boolean {
    const hasNumbers = NUMBERS_REG_EX.exec(password) !== null;
    const hasLetters = LETTERS_REG_EX.exec(password) !== null;
    const hasSymbols = SYMBOLS_REG_EX.exec(password) !== null;

    return (
      hasLetters &&
      hasNumbers &&
      hasSymbols &&
      PASSWORD_ALLOW_SYMBOLS_REG_EX.exec(password) !== null
    );
  }
  private _encodeUnicode(str: string): string {
    // используем encodeURIComponent, чтобы получить кодированный в процентах UTF-8,
    // затем конвертируем процентные кодировки в необработанные байты, которые
    // можно передать в btoa.
    // https://developer.mozilla.org/ru/docs/Web/API/WindowBase64/Base64_encoding_and_decoding
    return encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (match, p1: string) =>
      String.fromCharCode(parseInt(`0x${p1}`, 16))
    );
  }
  private async _executeCaptchaToken(action: string): Promise<string | undefined> {
    try {
      return await this._root.commonModule.executeCaptchaToken(action);
    } catch (error) {
      console.error(error);
    }
  }
}
